import PropTypes from 'prop-types';
import { Card, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';
import { mrrBreakdown } from '../../../helpers/mrr';

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

function KpiCard({ label, value, hint, icon, color = 'primary', deltas = [] }) {
  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon={icon} width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
            {hint && (
              <Tooltip title={hint}>
                <span style={{ display: 'inline-flex' }}>
                  <Iconify
                    icon="eva:info-outline"
                    width={14}
                    height={14}
                    sx={{ color: 'text.disabled', cursor: 'help' }}
                  />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="h4" noWrap>
            {value}
          </Typography>
          {deltas.map((d) => {
            const isUp = d.direction === 'up';
            const deltaColor = isUp ? 'success.main' : 'error.main';
            return (
              <Stack key={d.label} direction="row" spacing={0.25} alignItems="center" sx={{ width: 'fit-content' }}>
                <Iconify
                  icon={isUp ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'}
                  width={16}
                  height={16}
                  sx={{ color: deltaColor }}
                />
                <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }} noWrap>
                  {`${isUp ? '+' : '-'}${fEur(d.value)} ${d.label}`}
                </Typography>
                {d.hint && (
                  <Tooltip title={d.hint}>
                    <span style={{ display: 'inline-flex' }}>
                      <Iconify
                        icon="eva:info-outline"
                        width={13}
                        height={13}
                        sx={{ color: 'text.disabled', cursor: 'help' }}
                      />
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  deltas: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      direction: PropTypes.oneOf(['up', 'down']),
      label: PropTypes.string,
      hint: PropTypes.string,
    })
  ),
};

// Card del MRR de mensuales como evolución del mes (ver `helpers/mrr.js`):
//   MRR mes anterior − bajas que terminan este mes + nuevo MRR = MRR actual (valor grande)
//   MRR actual − bajas que terminan el mes que viene ≈ previsión del mes siguiente (dato del back)
function MrrCard({ prevMonth, lostPrev, newMrr, current, lostNextMrr, nextMonth, nextMonthDiff, nextMonthLabel }) {
  const diff = current - prevMonth;
  const pct = prevMonth > 0 ? (diff / prevMonth) * 100 : 0;
  const trendColor = diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary';

  const row = (label, value, hint, color, strong) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography
          variant={strong ? 'body2' : 'caption'}
          color={strong ? 'text.primary' : 'text.secondary'}
          sx={{ fontWeight: strong ? 600 : 400 }}
          noWrap
        >
          {label}
        </Typography>
        <HintIcon title={hint} size={13} />
      </Stack>
      <Typography
        variant={strong ? 'body2' : 'caption'}
        sx={{ color: color || 'text.primary', fontWeight: strong ? 700 : 600 }}
        noWrap
      >
        {value}
      </Typography>
    </Stack>
  );

  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: 'primary.lighter',
            color: 'primary.main',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon="eva:credit-card-outline" width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              MRR actual
            </Typography>
            <HintIcon title="MRR del mes anterior − bajas que terminan este mes + nuevo MRR. Solo suscripciones mensuales, sin anuales. Las bajas que terminan el mes que viene siguen contando: ya han pagado este mes." />
          </Stack>
          <Typography variant="h4" noWrap>
            {fEur(current)}
          </Typography>
          {diff !== 0 && (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Iconify
                icon={diff > 0 ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'}
                width={14}
                height={14}
                sx={{ color: trendColor }}
              />
              <Typography variant="caption" sx={{ color: trendColor, fontWeight: 700 }} noWrap>
                {`${pct > 0 ? '+' : ''}${pct.toFixed(1)}% vs mes anterior`}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        {row(
          'MRR mes anterior',
          fEur(prevMonth),
          'Aproximado: mensuales activas hoy sin las altas de este mes. Incluye las bajas programadas que terminan este mes (pagaron el mes pasado), pero no las bajas que ya se han hecho efectivas este mes, así que puede bajar a lo largo del mes según se aplican.'
        )}
        {lostPrev > 0 &&
          row(
            'Bajas del mes',
            `-${fEur(lostPrev)}`,
            'MRR de las mensuales con cancelación programada cuyo periodo termina este mes. Se cobraron el mes pasado y este mes ya no aportan.',
            'error.main'
          )}
        {row(
          'Nuevo MRR',
          `+${fEur(newMrr)}`,
          'MRR de las suscripciones mensuales dadas de alta este mes (sin las que tienen baja programada que termina este mes).',
          'success.main'
        )}

        <Divider sx={{ my: 0.5 }} />

        {lostNextMrr > 0 &&
          row(
            'Bajas del mes que viene',
            `-${fEur(lostNextMrr)}`,
            'MRR de las mensuales con cancelación programada cuyo periodo termina el mes que viene. Ya han pagado este mes, así que cuentan en el MRR actual; salen de la previsión.',
            'text.disabled'
          )}
        {row(
          `Previsión ${nextMonthLabel}`,
          fEur(nextMonth),
          `Dato del back (monthlyBillingNextMonth): mensuales activas sin baja programada. Es el MRR con el que arrancará el mes que viene si no hay más altas ni bajas; debería ser el MRR actual menos las bajas del mes que viene.${
            Math.abs(nextMonthDiff) >= 0.01
              ? ` Ahora no cuadra por ${fEur(nextMonthDiff)} (alguna baja sin fecha de fin o un cambio en el back).`
              : ''
          }`,
          Math.abs(nextMonthDiff) >= 0.01 ? 'warning.main' : undefined,
          true
        )}
      </Stack>
    </Card>
  );
}

MrrCard.propTypes = {
  prevMonth: PropTypes.number.isRequired,
  lostPrev: PropTypes.number.isRequired,
  newMrr: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  lostNextMrr: PropTypes.number.isRequired,
  nextMonth: PropTypes.number.isRequired,
  nextMonthDiff: PropTypes.number.isRequired,
  nextMonthLabel: PropTypes.string.isRequired,
};

function HintIcon({ title, size = 14 }) {
  return (
    <Tooltip title={title}>
      <span style={{ display: 'inline-flex' }}>
        <Iconify icon="eva:info-outline" width={size} height={size} sx={{ color: 'text.disabled', cursor: 'help' }} />
      </span>
    </Tooltip>
  );
}

HintIcon.propTypes = {
  title: PropTypes.string.isRequired,
  size: PropTypes.number,
};

export default function SubscriptionsKpis({ stats }) {
  if (!stats) return null;

  const monthlyBilling = Number(stats.monthlyBilling) || 0;

  // Horizonte de renewalsByMonth = 12 meses → cada anual activa aparece 1 vez,
  // así que la suma de `total` es la facturación anual total de las anuales activas.
  const annualBillingTotal = (stats.renewalsByMonth || []).reduce((sum, m) => sum + (Number(m?.total) || 0), 0);

  // Parte mensual = MRR actual (sin bajas programadas), mismo criterio que la card de MRR: si no,
  // el teórico iría bajando durante el mes según se aplican las cancelaciones.
  const mrr = mrrBreakdown(stats);
  const theoreticalMrr = mrr.current + annualBillingTotal / 12;
  const arr = theoreticalMrr * 12;

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthRenewals =
    Number((stats.renewalsByMonth || []).find((m) => m?.month === currentMonthKey)?.total) || 0;
  // Las nuevas suscripciones anuales del mes no están en renewalsByMonth (su renovación cae
  // dentro de un año), pero su primer cobro entra en caja este mes — así que suman aquí.
  const newAnnualThisMonth = Number(stats.newAnnualSubscriptionsThisMonth?.totalEUR) || 0;
  const currentMonthBilling = monthlyBilling + currentMonthRenewals + newAnnualThisMonth;
  const nextMonthLabel = new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString('es-ES', {
    month: 'long',
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Facturación del mes"
          value={fEur(currentMonthBilling)}
          icon="eva:pie-chart-2-outline"
          color="warning"
          hint="Cuotas de las mensuales activas (incluidas las que tienen baja programada, que aún cobran este mes) + renovaciones anuales que entran este mes + primer cobro de las nuevas suscripciones anuales del mes. Es la caja recurrente que entra por Stripe en el mes en curso."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MrrCard
          prevMonth={mrr.prevMonth}
          lostPrev={mrr.lostPrev}
          newMrr={mrr.newMrr}
          current={mrr.current}
          lostNextMrr={mrr.lostNextMrr}
          nextMonth={mrr.nextMonth}
          nextMonthDiff={mrr.nextMonthDiff}
          nextMonthLabel={nextMonthLabel}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="MRR teórico"
          value={fEur(theoreticalMrr)}
          icon="eva:trending-up-outline"
          color="info"
          hint="MRR actual (mensuales sin las bajas programadas) + facturación anual de las suscripciones anuales activas / 12. Es la métrica SaaS estándar (todo normalizado a base mensual)."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="ARR"
          value={fEur(arr)}
          icon="eva:calendar-outline"
          color="success"
          hint="MRR teórico × 12. Ingresos recurrentes anualizados con la base actual."
        />
      </Grid>
    </Grid>
  );
}

SubscriptionsKpis.propTypes = {
  stats: PropTypes.object,
};
